import { FC, memo, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useGame } from "@/providers/GameProvider"
import {
  CARD_SKIN_TYPES,
  useGetCardBackTemplateByTypeQuery
} from "@/store/apis/cardBacksAPI"
import { CardView } from "./CardView"
import { useCardAvailability } from "./hooks/useCardAvailability"
import { useCardInteractions } from "./hooks/useCardInteractions"
import { ICardProps } from "./types"
import { getCardViewSkin } from "./values"

const GameCardComponent: FC<ICardProps> = ({
  data,
  className,
  dataFlipId,
  owner,
  status,
  isShowFrontSide = true,
  isShowBackSide = true,
  isMatchActive,
  player,
  refIsMyTurn,
  refIsMatchActive,
  isShowActive: isShowActiveProp,
  onClick,
  onClose,
  onActivateCard,
  ...props
}) => {
  const { t, ready } = useTranslation("cards")
  const { playerSkin, opponentSkin, gameState, isMe } = useGame()

  const { data: goldCardTemplate } = useGetCardBackTemplateByTypeQuery(
    CARD_SKIN_TYPES.GOLD,
    {
      skip: data?.tier !== "gold"
    }
  )

  const cardBackData = useMemo(() => {
    if (data?.tier === "gold") {
      return goldCardTemplate
    }

    if (
      ["opponent-view", "opponent", "removed-opponent"].includes(owner || "")
    ) {
      return opponentSkin?.cardBack
    }

    if (["active", "archive", "preview"].includes(owner || "")) {
      if (isMe(data?.ownerId)) {
        return playerSkin?.cardBack
      } else {
        return opponentSkin?.cardBack
      }
    }

    return playerSkin?.cardBack
  }, [owner, opponentSkin, playerSkin, gameState, data, goldCardTemplate])

  const { isAvailableToUse, isShowActive } = useCardAvailability({
    data,
    player,
    owner,
    isMatchActive
  })

  const resolvedIsShowActive = isShowActiveProp ?? isShowActive

  const title = useMemo(() => {
    if (!data?.type) return ""
    return t(`card.${data.type}.title.nominative`)
  }, [data?.type, t, ready])

  const description = useMemo(() => {
    if (!data?.type) return ""

    if (owner === "preview") {
      return t(`card.${data.type}.description`)
    }

    return t(`card.${data.type}.description_short`)
  }, [data?.type, owner, t, ready])

  const { refCard, refContent, onCardClick, onCardTouchEnd } =
    useCardInteractions({
      owner,
      status,
      isAvailableToUse,
      refIsMyTurn,
      refIsMatchActive,
      onClick,
      onClose,
      onActivateCard
    })

  return (
    <CardView
      {...props}
      className={className}
      data={data}
      dataFlipId={dataFlipId}
      owner={owner}
      status={status}
      isShowFrontSide={isShowFrontSide}
      isShowBackSide={isShowBackSide}
      isMatchActive={isMatchActive}
      isShowActive={resolvedIsShowActive}
      title={title}
      description={description}
      skin={getCardViewSkin(cardBackData)}
      cardRef={refCard}
      contentRef={refContent}
      onCardClick={onCardClick}
      onCardTouchEnd={onCardTouchEnd}
    />
  )
}

export const GameCard = memo(GameCardComponent)
